import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ErrorIcon, MainHeader } from '@eposnow/ui-core';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { AlertColor, Box, Button, Fade, Link, Paper, Popper, Typography } from '@mui/material';
import { useAuth } from 'oidc-react';
import GeneratedReportsTable from './GeneratedReportsTable';
import { Report } from '../../../models/report';
import { UserContext } from '../../../context/UserContext';
import { useDeleteGeneratedReport, useGeneratedReports, toggleGeneratedReportEnabled } from '../../../api/generatedReports';
import EnhancedSnackbar from '../../../components/EnhancedSnackbar';
import GenericErrorPage from '../../settings/GenericErrorPage';
import { ReactComponent as ServerIcon } from '../../../img/ServerIcon.svg';
import { ReactComponent as UnderMaintenance } from '../../../img/underMaintenance.svg';
import { ErrorObject } from '../../../api/fetch';
import GenerateReportOverviewPanel from '../components/GenerateReportOverviewPanel';
import FeedbackSection from '../components/FeedbackSection';
import { ReportOverviewItem, useReportOverview } from '../../../api/reportOverview';

const GeneratedReports = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useContext(UserContext);
  const navigate = useNavigate();
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const { data: reportOverview, refetch: reportOverviewRefetch } = useReportOverview<ReportOverviewItem[]>('ReportOverview');

  const handleEdit = useCallback((row: Report) => {
    setEditingReport(row);
    setOpen(true);
  }, []);

  let learnMoreUrl = `${process.env.REACT_APP_LINK_SUPPORT
    }s/article/Data-Downloader?language=${locale.substring(0, 2)}`;
  if (locale.substring(0, 2) === 'es') {
    learnMoreUrl = `${process.env.REACT_APP_LINK_SUPPORT}s/article/Data-Downloader?language=es`;
  }

  interface SnackbarConfig {
    open: boolean;
    severity: AlertColor;
    message: string;
  }

  const [snackbar, setSnackbarConfig] = useState<SnackbarConfig>({
    open: false,
    severity: 'info',
    message: '',
  });

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const {
    isError,
    isLoading,
    error,
    data: reports = [],
    refetch
  } = useGeneratedReports<Report[]>('GeneratedReports', debouncedSearch);

  const location = useLocation();

  useEffect(() => {
    refetch();
  }, [location.key]);

  const handleRefresh = useCallback(() => {
    if (isLoading || isError) return;

    reportOverviewRefetch();

    refetch({ throwOnError: true })
      .then(() => {
        setSnackbarConfig({
          open: true,
          severity: 'success',
          message: t('screens.dataDownloader.refreshSuccess'),
        });
      })
      .catch(() => {
        setSnackbarConfig({
          open: true,
          severity: 'error',
          message: t('components.error.somethingFailed'),
        });
      });
  }, [refetch, t, isError, isLoading]);

  const deleteReport = useDeleteGeneratedReport();
  const handleDeleteReport = (reportId: string | undefined) => {
    if (!reportId) return;

    deleteReport(reportId)
      .then(() => {
        refetch();
        setSnackbarConfig({
          open: true,
          severity: 'success',
          message: t('screens.dataDownloader.deleteSuccess'),
        });
      })
      .catch(() => {
        setSnackbarConfig({
          open: true,
          severity: 'error',
          message: t('components.error.somethingFailed'),
        });
      });
  };

  const handleChangeSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleToggle = useCallback(
    (scheduleId: string, enabled: boolean) => {
      toggleGeneratedReportEnabled(scheduleId, enabled, maybeAccessToken)
        .then(() => {
          setSnackbarConfig({
            open: true,
            severity: 'success',
            message: t('screens.dataDownloader.updateSuccess'),
          });
        })
        .catch(() => {
          setSnackbarConfig({
            open: true,
            severity: 'error',
            message: t('components.error.somethingFailed'),
          });
        });
    },
    [maybeAccessToken, t]
  );

  if (process.env.REACT_APP_DATA_DOWNLOADER_MAINTENANCE === 'true')
    return (
      <GenericErrorPage
        icon={<UnderMaintenance />}
        title={t('components.iconMessage.errorPage.underMaintenance')}
        description={t('components.iconMessage.errorPage.underMaintenanceDescription')}
        rightButtonText={t('components.iconMessage.errorPage.back')}
        actionRightButton={() => navigate(-1)}
      />
    );

  if (isError) {
    const status = (error as ErrorObject)?.code;

    if (status === 400) {
      return (
        <GenericErrorPage
          icon={<ErrorIcon />}
          title={t('components.iconMessage.errorPage.badRequest')}
          description={t('components.iconMessage.errorPage.noProcessRequest')}
          leftButtonText={t('components.iconMessage.errorPage.back')}
          rightButtonText={t('components.iconMessage.errorPage.retry')}
          actionLeftButton={() => navigate(-1)}
          actionRightButton={() => refetch()}
        />
      );
    }

    if (status === 429) {
      return (
        <GenericErrorPage
          icon={<ServerIcon />}
          title={t('components.iconMessage.errorPage.serverBusy')}
          description={t('components.iconMessage.errorPage.serveBusyText')}
          leftButtonText={t('components.iconMessage.errorPage.back')}
          rightButtonText={t('components.iconMessage.errorPage.retry')}
          actionLeftButton={() => navigate(-1)}
          actionRightButton={() => refetch()}
        />
      );
    }

    return (
      <GenericErrorPage
        icon={<ServerIcon />}
        title={t('components.iconMessage.errorPage.internalServerError')}
        description={t('components.iconMessage.errorPage.internalServerErrorText')}
        leftButtonText={t('components.iconMessage.errorPage.status')}
        rightButtonText={t('components.iconMessage.errorPage.back')}
        actionLeftButton={() => {
          window.location.href = `${process.env.REACT_APP_LINK_STATUS_PAGE}`;
        }}
        actionRightButton={() => navigate(-1)}
      />
    );
  }

  return (
    <>
      <FeedbackSection text={t('actions.feedbackDownloader')}/>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginX: '112px',
          marginY: '48px',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '18px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <MainHeader title={t('screens.dataDownloader.generatedReportsTitle')} theme={theme} />
            <Button
              variant="contained"
              color="primary"
              to="/reports-overview"
              component={RouterLink}
              role="button"
            >
              {t('screens.dataDownloader.generatedReportsButton')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: '8px' }}>
            <Typography variant="body1">
              {t('screens.dataDownloader.generatedReportsDescription')}
              <Link
                href={learnMoreUrl}
                sx={{ textDecoration: 'underline', marginLeft: '0.3rem', fontWeight: '600' }}
              >
                {t('screens.transactions.learnMore')}
              </Link>
            </Typography>
          </Box>
        </Box>
        <GeneratedReportsTable
          reports={reports}
          handleDeleteReport={handleDeleteReport}
          handleRefresh={handleRefresh}
          handleChangeSearch={handleChangeSearch}
          handleToggle={handleToggle}
          onEdit={handleEdit}
          searchValue={searchValue}
        />
        <EnhancedSnackbar
          open={snackbar.open}
          severity={snackbar.severity}
          message={snackbar.message}
          onClose={() => setSnackbarConfig({ ...snackbar, open: false })}
          forceColor={
            snackbar.severity === 'success'
              ? theme.palette.success['160p']
              : theme.palette.error['160p']
          }
        />
      </Box>

      <Popper
        open={open}
        anchorEl={document.body}
        placement="bottom-end"
        transition
      >
        <Fade mountOnEnter unmountOnExit in>
          <Paper
            sx={{
              width: '450px',
              height: 'calc(100vh - 315px)',
              marginTop: '310px',
              marginRight: '8px',
              position: 'fixed',
              right: '-16px',
              top: 0,
              overflow: 'auto',
              borderRadius: '0px',
              boxShadow: `
                0px 8px 10px -5px rgba(0, 0, 0, 0.2),
                0px 16px 24px 2px rgba(0, 0, 0, 0.14),
                0px 6px 30px 5px rgba(0, 0, 0, 0.12)
              `,
              clipPath: 'inset(0px 0px 0px -74px)',
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
            }}
          >
            {editingReport && (
              <GenerateReportOverviewPanel
                reportSource={editingReport}
                reportTypes={reportOverview}
                close={(snackbarMessage?: { message: string, severity: 'success' | 'error' }) => {
                  setOpen(false);
                  setEditingReport(null);

                  if (snackbarMessage?.message) {
                    setSnackbarConfig({ open: true, severity: snackbarMessage.severity, message: snackbarMessage.message });
                  }
                }}
              />
            )}
          </Paper>
        </Fade>
      </Popper>
    </>
  );
};

export default GeneratedReports;
